import { useMemo } from 'react';
import { BeeBaseAlert } from '../../types';
import { haversineDistance } from '../../utils/geo';
import { Apiary } from '../../types';

export type RiskLevel = 'High' | 'Medium' | 'Low';

export interface RiskMatch {
  apiaryId: string;
  apiaryName: string;
  alertId: string;
  disease: string;
  distanceKm: number;
  risk: RiskLevel;
  alertDate?: string;
  location?: string;
}

/**
 * Determine risk level based on disease type and distance
 */
function getRiskLevel(disease: string, distanceKm: number): RiskLevel {
  const severeDiseases = ['AFB', 'American Foulbrood', 'EFB', 'European Foulbrood'];
  const moderateDiseases = ['Varroa', 'Varroa destructor', 'Tropilaelaps'];
  
  // High risk: severe diseases within 5km or any disease within 2km
  if ((severeDiseases.some(d => disease.includes(d)) && distanceKm <= 5) || distanceKm <= 2) {
    return 'High';
  }
  
  // Medium risk: moderate diseases within 8km or severe diseases within 15km
  if ((moderateDiseases.some(d => disease.includes(d)) && distanceKm <= 8) || 
      (severeDiseases.some(d => disease.includes(d)) && distanceKm <= 15)) {
    return 'Medium';
  }
  
  // Low risk: everything else within the radius
  return 'Low';
}

/**
 * Hook to calculate risk matches between apiaries and BeeBase alerts
 */
export function useBeeBaseRisk(apiaries: Apiary[], alerts: BeeBaseAlert[], radiusKm = 10) {
  const matches = useMemo<RiskMatch[]>(() => {
    const results: RiskMatch[] = [];
    
    for (const apiary of apiaries) {
      for (const alert of alerts) {
        if (!alert.lat || !alert.lng) continue;
        
        const dist = haversineDistance(apiary.lat, apiary.lng, alert.lat, alert.lng);
        
        if (dist <= radiusKm) {
          results.push({
            apiaryId: apiary.id,
            apiaryName: apiary.name,
            alertId: alert.id,
            disease: alert.disease || 'Unknown',
            distanceKm: parseFloat(dist.toFixed(2)),
            risk: getRiskLevel(alert.disease || 'Unknown', dist),
            alertDate: alert.date || undefined,
            location: alert.location || undefined
          });
        }
      }
    }
    
    return results;
  }, [apiaries, alerts, radiusKm]);

  return matches;
}
